import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import AnimatedSection from '../components/AnimatedSection';
import { contactInfo } from '../mockData';
import { trackButtonClick, trackFormSubmit, trackPageView } from '../utils/analytics';
import { submitFormToLambda } from '../utils/api';
import { toast as sonnerToast } from 'sonner';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    trackPageView('/contact', 'Contact Us');
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track form submission
    trackFormSubmit('contact_form', { name: formData.name });

    try {
      // Submit to Lambda API
      await submitFormToLambda('contact_form', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      });

      toast({
        title: 'Message Sent!',
        description: 'Thank you for contacting us. We\'ll get back to you shortly.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      sonnerToast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1F44] to-[#0056D2] py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Have questions? We're here to help. Reach out to our team today.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[#F5F7FA]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <AnimatedSection animation="slideInLeft">
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-bold text-[#0A1F44] mb-6">
                    Contact Information
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    Our team is ready to assist you with any questions about our services.
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Email */}
                  <div className="flex items-start space-x-4 bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="bg-[#0056D2] p-3 rounded-xl">
                      <Mail size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A1F44] mb-1">Email</h3>
                      <a
                        href={`mailto:${contactInfo.email}`}
                        className="text-[#0056D2] hover:underline"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start space-x-4 bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="bg-[#FF7A00] p-3 rounded-xl">
                      <Phone size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A1F44] mb-1">Phone</h3>
                      <a
                        href={`tel:${contactInfo.phone1}`}
                        className="text-gray-700 hover:text-[#0056D2] block"
                      >
                        {contactInfo.phone1}
                      </a>
                      <a
                        href={`tel:${contactInfo.phone2}`}
                        className="text-gray-700 hover:text-[#0056D2] block"
                      >
                        {contactInfo.phone2}
                      </a>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start space-x-4 bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="bg-[#0056D2] p-3 rounded-xl">
                      <MapPin size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A1F44] mb-1">Address</h3>
                      <p className="text-gray-700">{contactInfo.address}</p>
                    </div>
                  </div>

                  {/* Support Hours */}
                  <div className="flex items-start space-x-4 bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="bg-[#FF7A00] p-3 rounded-xl">
                      <Clock size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A1F44] mb-2">Support Hours</h3>
                      <div className="space-y-1">
                        <p className="text-gray-700"><span className="font-medium text-[#0A1F44]">Sales:</span> Mon–Sat, 10 AM – 7 PM</p>
                        <p className="text-gray-700"><span className="font-medium text-[#0A1F44]">Support:</span> 24/7 (Chat, Phone, Email)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Contact Form */}
            <AnimatedSection animation="slideInRight">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-6">
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                      Phone Number *
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                      placeholder="+91 98XXXXXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                      placeholder="How can we help you?"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => trackButtonClick('submit_contact_form', 'contact_form')}
                    className="w-full bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold py-6 text-lg rounded-full transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
                  >
                    <Send size={20} />
                    <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  </Button>
                </form>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Visit Our Office
              </h2>
              <p className="text-xl text-gray-600">
                Come say hello at our office headquarters
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="scaleIn" delay={200}>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3576.9826924393647!2d73.02836!3d26.28894!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDE3JzIwLjIiTiA3M8KwMDEnNDIuMSJF!5e0!3m2!1sen!2sin!4v1234567890123"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="TallyBharat Office Location"
              />
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Contact;